"use client";

import { Close, useTailwind, Button } from "@oreme/ui";
import { useSetAtom } from "jotai";
import { markerPopupOffset, RMarker, RPopup } from "maplibre-react-components";
import React, { type ReactElement } from "react";
import { activeStationAtom } from "@/store/weather";
import type { WeatherStation } from "@/types";
import { useTranslations } from "next-intl";

interface WeatherStationPopupProps {
  station: WeatherStation;
}

function WeatherStationPopup({
  station,
}: Readonly<WeatherStationPopupProps>): ReactElement {
  const t = useTranslations("weather.StationPopup");
  const tailwind = useTailwind();
  const color = tailwind.theme.colors.blue[500];
  const setActiveStation = useSetAtom(activeStationAtom);
  const latitude = Number(station.LAT_DG);
  const longitude = Number(station.LON_DG);

  const handleClose = (): void => {
    setActiveStation(null);
  };

  return (
    <>
      <RPopup
        className="flex flex-col gap-1 min-w-48 w-auto p-0 m-0 rounded-md bg-background"
        latitude={latitude}
        longitude={longitude}
        offset={markerPopupOffset}
      >
        <header className="flex flex-row gap-2 items-center p-2 border-b justify-between">
          <h4
            className="font-semibold text-sm truncate"
            title={station.NOM_USUEL}
          >
            {station.NOM_USUEL}
          </h4>
          <Button
            size="icon-xs"
            onClick={handleClose}
            variant="ghost"
            className="border-0"
          >
            <Close title={t("close")} />
            <span className="sr-only">{t("close")}</span>
          </Button>
        </header>
        <p className="p-2 pb-0 text-sm text-muted-foreground">
          {station.NUM_POSTE}
        </p>
        <p className="px-2 pb-0 text-sm text-muted-foreground">
          {station.COMMUNE}
        </p>
        <div className="flex flex-col gap-2 p-2">
          <dl className="grid grid-cols-2 gap-0.5 text-xs border border-border rounded p-1 bg-accent-background">
            <dt>Latitude</dt>
            <dd className="text-right">{station.LAT_DG}</dd>
            <dt>Longitude</dt>
            <dd className="text-right">{station.LON_DG}</dd>
            {station.ALTI ? (
              <>
                <dt>Altitude</dt>
                <dd className="text-right">{station.ALTI} m</dd>
              </>
            ) : null}
          </dl>
        </div>
      </RPopup>
      <RMarker
        initialColor={color}
        key={`station-marker-${station.NOM_USUEL}`}
        latitude={latitude}
        longitude={longitude}
      />
    </>
  );
}

export { WeatherStationPopup, type WeatherStationPopupProps };
